import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { Button, Tooltip } from '@mui/material';
import { forwardRef, useRef, useImperativeHandle } from 'react';

const StyledButton = styled(Button, {
	shouldForwardProp: (prop) => prop !== 'variantType',
})(({ variantType }) => {
	const variants = {
		default: {
			backgroundColor: '#2a5d8a',

			'&:hover': {
				backgroundColor: '#1e4a70',
			},
		},
		accept: {
			backgroundColor: '#2a5d8a',

			'&:hover': {
				backgroundColor: '#1e4a70',
			},
		},
		approve: {
			backgroundColor: '#2a5d8a',

			'&:hover': {
				backgroundColor: '#1e4a70',
			},
		},
		upgrade: {
			backgroundColor: 'rgb(3, 57, 108)',

			'&:hover': {
				backgroundColor: 'rgb(3, 57, 108)',
			},
		},
		claim: {
			backgroundColor: '#2a5d8a',

			'&:hover': {
				backgroundColor: '#1e4a70',
			},
		},
		reject: {
			backgroundColor: '#b91c1c',

			'&:hover': {
				backgroundColor: '#991b1b',
			},
		},
		save: {
			backgroundColor: 'rgb(3, 57, 108)',

			'&:hover': {
				backgroundColor: 'rgb(3, 57, 108)',
			},
		},
		return: {
			backgroundColor: '#6b89a8',

			'&:hover': {
				backgroundColor: '#57718a',
			},
		},
		add: {
			backgroundColor: '#00914f',

			'&:hover': {
				backgroundColor: '#00914f',
			},
		},
		yes: {
			width: '6rem',
			color: '#ffffff',
			padding: '6px 16px',
			borderRadius: '4px',
			backgroundColor: '#00914f',
			transition: 'all 0.1s ease-in-out',

			'&:hover': { backgroundColor: '#00914f', transform: 'scale(1.05)' },
		},
		no: {
			width: '6rem',
			color: '#ffffff',
			borderRadius: '4px',
			padding: '6px 16px',
			backgroundColor: '#B00020',
			transition: 'all 0.1s ease-in-out',

			'&:hover': { backgroundColor: '#B00020', transform: 'scale(1.05)' },
		},
		edit: {
			padding: '4px 12px',
			border: '1px solid',
			borderColor: '#d9d9d9',
			backgroundColor: '#fff',
			transition: 'all 0.25s ease',
			color: 'rgba(0, 0, 0, 0.88)',

			'&:hover': {
				color: '#4096ff',
				transform: 'scale(1.05)',
				borderColor: '#4096ff',
				backgroundColor: '#fff',
			},

			'&:active': {
				backgroundColor: '#e6f4ff',
			},

			'&.Mui-disabled': {
				borderColor: '#d9d9d9',
				backgroundColor: '#f5f5f5',
				color: 'rgba(0, 0, 0, 0.25)',
			},
		},
	};

	return {
		color: 'white',
		fontWeight: 400,
		padding: '5px 16px',
		borderRadius: '4px',
		textTransform: 'none',
		lineHeight: '22px',

		...(variants[variantType] || variants.default),
	};
});

const ActionButton = ({ sx, icon, label, tooltip, onClick, ref, variantType, disabled = false, ...props }) => {
	const innerRef = useRef(null);

	useImperativeHandle(
		ref,
		() => ({
			focus: () => innerRef.current?.focus(),
			click: () => innerRef.current?.click(),
			// optionally expose the button DOM node itself
			getNode: () => innerRef.current,
		}),
		[]
	);

	const buttonEl = (
		<StyledButton
			ref={innerRef}
			onClick={onClick}
			disabled={disabled}
			variant="contained"
			variantType={variantType}
			startIcon={icon || undefined}
			sx={{
				...sx,
			}}
			{...props}
		>
			{label}
		</StyledButton>
	);

	return tooltip ? (
		<Tooltip title={tooltip}>
			<span>{buttonEl}</span>
		</Tooltip>
	) : (
		buttonEl
	);
};

ActionButton.displayName = 'ActionButton';

ActionButton.propTypes = {
	icon: PropTypes.node,
	sx: PropTypes.object,
	type: PropTypes.string,
	onClick: PropTypes.func,
	label: PropTypes.string,
	disabled: PropTypes.bool,
	tooltip: PropTypes.string,
	variantType: PropTypes.oneOf(['default', 'accept', 'approve', 'claim', 'reject', 'return', 'add', 'save', 'upgrade', 'yes', 'no', 'edit']),
};

export default ActionButton;
